import React, { useState, useEffect, useRef } from 'react';
import {
  UploadCloud, Search, Trash2, Copy, Check, Eye, X, Image as ImageIcon,
  AlertCircle, RefreshCw, HardDrive, CheckCircle2
} from 'lucide-react';
import { adminApi } from '../../lib/adminApi';
import { getBlogImageUrl } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import BlogNavTabs from './BlogNavTabs';
import './AdminBlog.css';

export default function AdminBlogUploads({ isModal = false, onSelectImage = null }) {
  const [media, setMedia] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [deleteConfirmItem, setDeleteConfirmItem] = useState(null);
  const [previewItem, setPreviewItem] = useState(null);

  const fileInputRef = useRef(null);

  const fetchMedia = async () => {
    setLoading(true);
    try {
      const res = await adminApi.get('/admin/blog-media', {
        params: { search: search || undefined, limit: 100 }
      });
      setMedia(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load uploads.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMedia();
  }, [search]);

  // Robust upload with automatic base64 fallback (supports up to 50MB)
  const handleUploadFile = async (file) => {
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      setError("Image exceeds maximum size of 50MB.");
      return;
    }
    setUploading(true);
    setError('');
    setSuccess('');

    // Strategy 1: Multipart upload (let Axios set boundary automatically)
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await adminApi.post('/admin/blog-upload', formData);
      if (res.data?.url) {
        setSuccess(`Uploaded ${file.name} successfully!`);
        setTimeout(() => setSuccess(''), 4000);
        fetchMedia();
        if (onSelectImage) onSelectImage(getBlogImageUrl(res.data.url));
        return;
      }
    } catch (err1) {
      console.warn("Multipart upload failed, attempting base64 fallback...", err1);
      // Strategy 2: Base64 JSON upload fallback (bypasses proxy/multipart issues)
      try {
        const base64Data = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        const res2 = await adminApi.post('/admin/blog-upload-base64', {
          filename: file.name,
          data: base64Data
        });

        if (res2.data?.url) {
          setSuccess(`Uploaded ${file.name} successfully!`);
          setTimeout(() => setSuccess(''), 4000);
          fetchMedia();
          if (onSelectImage) onSelectImage(getBlogImageUrl(res2.data.url));
          return;
        }
      } catch (err2) {
        setError(err2.response?.data?.detail || err1.response?.data?.detail || "Upload failed. Please check file size and type.");
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleUploadFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleUploadFile(file);
  };

  const handleCopyUrl = (url, id) => {
    const resolved = getBlogImageUrl(url);
    const fullUrl = resolved.startsWith('http') ? resolved : `${window.location.origin}${resolved}`;
    navigator.clipboard.writeText(fullUrl).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const handleDelete = async (item) => {
    try {
      await adminApi.delete(`/admin/blog-media/${item.id}`);
      setMedia(prev => prev.filter(m => m.id !== item.id));
      setTotal(prev => Math.max(0, prev - 1));
      setDeleteConfirmItem(null);
    } catch (err) {
      alert("Failed to delete media file.");
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  const formatDate = (isoStr) => {
    if (!isoStr) return '';
    try {
      return new Date(isoStr).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric'
      });
    } catch {
      return '';
    }
  };

  return (
    <div className={isModal ? 'admin-uploads-modal-body p-4' : 'admin-blogs-container max-w-7xl mx-auto pb-16'}>
      {!isModal && (
        <BlogNavTabs
          title="Uploads"
          description="Manage all uploaded blog images, featured banners, and media assets."
          showCreateButton={false}
        />
      )}

      {/* Action / Search Header */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-6 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3 w-full md:w-auto">
          <div className="relative flex-1 md:w-72">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search uploads..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>
          <span className="text-xs font-semibold px-2.5 py-1 bg-slate-100 text-slate-600 rounded-full whitespace-nowrap">
            {total} {total === 1 ? 'file' : 'files'}
          </span>
        </div>

        <div className="flex items-center gap-3 w-full md:w-auto justify-end">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept="image/*,.jfif,.avif"
            className="hidden"
          />
          <Button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="admin-primary flex items-center gap-2 font-semibold shadow-sm w-full md:w-auto"
          >
            {uploading ? (
              <>
                <RefreshCw size={16} className="animate-spin" />
                <span>Uploading...</span>
              </>
            ) : (
              <>
                <UploadCloud size={16} />
                <span>Upload image</span>
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Notifications */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg flex items-center gap-2">
          <AlertCircle size={18} className="shrink-0 text-red-500" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm rounded-lg flex items-center gap-2">
          <CheckCircle2 size={18} className="shrink-0 text-emerald-500" />
          <span>{success}</span>
        </div>
      )}

      {/* Drag and Drop Zone */}
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className="mb-8 border-2 border-dashed border-slate-300 hover:border-amber-500 bg-slate-50/60 hover:bg-amber-50/20 rounded-xl p-8 text-center cursor-pointer transition-all"
      >
        <div className="mx-auto w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 mb-3">
          <UploadCloud size={24} />
        </div>
        <p className="text-sm font-semibold text-slate-800">
          Click to upload or drag & drop images here
        </p>
        <p className="text-xs text-slate-500 mt-1">
          Supports PNG, JPG, JPEG, WEBP, GIF, SVG, AVIF, JFIF (up to 50MB)
        </p>
      </div>

      {/* Media Grid */}
      {loading ? (
        <div className="min-h-[300px] flex items-center justify-center">
          <div className="admin-spinner" />
        </div>
      ) : media.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center shadow-sm">
          <div className="w-16 h-16 bg-slate-100 text-slate-400 rounded-full flex items-center justify-center mx-auto mb-4">
            <ImageIcon size={32} />
          </div>
          <h3 className="text-lg font-bold text-slate-900 mb-1">No uploads yet</h3>
          <p className="text-sm text-slate-500 max-w-sm mx-auto mb-6">
            Upload featured banners, charts, or content images to use across your blog posts.
          </p>
          <Button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="admin-primary font-semibold inline-flex items-center gap-2"
          >
            <UploadCloud size={16} />
            <span>Upload first image</span>
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {media.map((item) => (
            <div
              key={item.id}
              className="group bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all flex flex-col relative"
            >
              {/* Thumbnail Container */}
              <div
                className="aspect-square bg-slate-100 relative overflow-hidden flex items-center justify-center cursor-pointer"
                onClick={() => {
                  if (onSelectImage) {
                    onSelectImage(getBlogImageUrl(item.url));
                  } else {
                    setPreviewItem(item);
                  }
                }}
              >
                <img
                  src={getBlogImageUrl(item.url)}
                  alt={item.filename}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 24 24" fill="none" stroke="%2394a3b8" stroke-width="2"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>';
                  }}
                />

                {onSelectImage && (
                  <div className="absolute inset-0 bg-amber-500/80 opacity-0 group-hover:opacity-100 flex items-center justify-center text-slate-950 font-bold text-xs transition-opacity">
                    Click to Select
                  </div>
                )}
              </div>

              {/* Info Body */}
              <div className="p-3 flex-1 flex flex-col justify-between">
                <div>
                  <p
                    className="text-xs font-semibold text-slate-800 truncate"
                    title={item.filename}
                  >
                    {item.filename}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    {formatFileSize(item.size_bytes)} {item.created_at && `• ${formatDate(item.created_at)}`}
                  </p>
                </div>

                {/* Card Actions */}
                <div className="flex items-center justify-between gap-1 mt-3 pt-2 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => handleCopyUrl(item.url, item.id)}
                    className="flex items-center gap-1 text-[11px] text-slate-600 hover:text-amber-600 font-medium py-1 px-1.5 rounded transition-colors"
                    title="Copy Image URL"
                  >
                    {copiedId === item.id ? (
                      <>
                        <Check size={12} className="text-emerald-600" />
                        <span className="text-emerald-600 font-bold">Copied</span>
                      </>
                    ) : (
                      <>
                        <Copy size={12} />
                        <span>Copy URL</span>
                      </>
                    )}
                  </button>

                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setPreviewItem(item)}
                      className="p-1 text-slate-400 hover:text-slate-700 rounded transition-colors"
                      title="View Image"
                    >
                      <Eye size={13} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleteConfirmItem(item)}
                      className="p-1 text-slate-400 hover:text-red-600 rounded transition-colors"
                      title="Delete Image"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6 text-center border border-slate-200">
            <div className="w-12 h-12 rounded-full bg-red-100 text-red-600 flex items-center justify-center mx-auto mb-4">
              <Trash2 size={24} />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Delete this image?</h3>
            <p className="text-sm text-slate-600 mb-6 truncate px-2 font-mono text-xs">
              {deleteConfirmItem.filename}
            </p>
            <div className="flex gap-3 justify-center">
              <Button
                variant="outline"
                onClick={() => setDeleteConfirmItem(null)}
                className="font-medium"
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleDelete(deleteConfirmItem)}
                className="bg-red-600 hover:bg-red-700 text-white font-medium"
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Full Image Preview Modal */}
      {previewItem && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm"
          onClick={() => setPreviewItem(null)}
        >
          <div
            className="bg-white rounded-xl max-w-3xl w-full overflow-hidden shadow-2xl relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h4 className="text-sm font-bold text-slate-900 truncate pr-4">
                {previewItem.filename}
              </h4>
              <button
                type="button"
                onClick={() => setPreviewItem(null)}
                className="p-1 text-slate-400 hover:text-slate-700 rounded"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-4 bg-slate-900/10 flex items-center justify-center max-h-[70vh] overflow-auto">
              <img
                src={getBlogImageUrl(previewItem.url)}
                alt={previewItem.filename}
                className="max-h-[60vh] max-w-full object-contain rounded"
              />
            </div>
            <div className="p-4 flex items-center justify-between bg-slate-50 border-t border-slate-200">
              <span className="text-xs text-slate-500 font-mono">
                {getBlogImageUrl(previewItem.url)}
              </span>
              <Button
                type="button"
                onClick={() => handleCopyUrl(previewItem.url, previewItem.id)}
                className="admin-primary text-xs flex items-center gap-1.5"
              >
                <Copy size={13} />
                <span>Copy URL</span>
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
